import { useId, useMemo, useRef, useState, type CSSProperties } from 'react';
import type { TagEntity } from '../../../entities/task/model/task.types';
import { TAG_COLORS } from '../../../shared/lib/constants';
import { Button } from '../../../shared/ui/Button';

export interface TagPickerProps {
  allTags: TagEntity[];
  selectedIds: string[];
  /** Смена связей с задачей: добавление/снятие (unlink task_tag). */
  onChange: (ids: string[]) => void;
  onCreateTag?: (name: string, color: string) => Promise<string | null>;
  allowCreate?: boolean;
  label?: string;
  compact?: boolean;
}

/**
 * Выбор тегов: выбранные + доступные, одинаковый размер чипов.
 * Крестик у выбранного тега = снять с задачи (unlink), не удаление тега.
 * Глобальное удаление тега — только в меню управления тегами.
 */
export function TagPicker({
  allTags,
  selectedIds,
  onChange,
  onCreateTag,
  allowCreate = true,
  label = 'Теги',
  compact = false,
}: TagPickerProps) {
  const listId = useId();
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState<string>(TAG_COLORS[8] ?? '#38bdf8');
  const nameRef = useRef<HTMLInputElement | null>(null);

  const sorted = useMemo(
    () => [...allTags].sort((a, b) => a.name.localeCompare(b.name, 'ru')),
    [allTags],
  );
  const selected = useMemo(
    () => sorted.filter((tag) => selectedIds.includes(tag.id)),
    [sorted, selectedIds],
  );
  const unselected = useMemo(
    () => sorted.filter((tag) => !selectedIds.includes(tag.id)),
    [sorted, selectedIds],
  );

  function unlink(tagId: string): void {
    onChange(selectedIds.filter((id) => id !== tagId));
  }

  function link(tagId: string): void {
    if (selectedIds.includes(tagId)) {
      return;
    }
    onChange([...selectedIds, tagId]);
  }

  async function handleCreate(): Promise<void> {
    const name = newName.trim();
    if (name.length === 0 || onCreateTag === undefined) {
      return;
    }
    const tagId = await onCreateTag(name, newColor);
    if (tagId !== null) {
      // createTag может вернуть уже существующий тег — не дублируем selectedIds.
      onChange(selectedIds.includes(tagId) ? selectedIds : [...selectedIds, tagId]);
      setNewName('');
      setCreating(false);
    }
  }

  function chipClass(isSelected: boolean): string {
    return [
      'inline-flex h-7 items-center gap-1 rounded-full border px-2 text-xs',
      isSelected ? 'ring-1 ring-tg-accent/60' : '',
    ]
      .filter(Boolean)
      .join(' ');
  }

  function chipStyle(tag: TagEntity): CSSProperties {
    return {
      backgroundColor: `${tag.color}26`,
      borderColor: `${tag.color}80`,
      color: tag.color,
    };
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className="tg-label mb-0">{label}</span>
        {allowCreate ? (
          <button
            type="button"
            className="text-xs text-tg-accent hover:text-tg-accent-hover"
            onClick={() => {
              setCreating((value) => !value);
              window.setTimeout(() => {
                nameRef.current?.focus();
              }, 0);
            }}
          >
            {creating ? 'Скрыть форму' : 'Создать тег'}
          </button>
        ) : null}
      </div>

      {creating ? (
        <div className={`rounded-xl border border-tg-border-soft bg-tg-bg p-3 space-y-3 ${compact ? '' : ''}`}>
          <input
            ref={nameRef}
            className="tg-input"
            value={newName}
            maxLength={80}
            placeholder="Название тега"
            aria-label="Название нового тега"
            onChange={(event) => {
              setNewName(event.target.value);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                void handleCreate();
              }
            }}
          />
          <div>
            <p className="tg-label mb-2">Цвет</p>
            <div id={listId} className="flex flex-wrap gap-1.5" role="listbox" aria-label="Палитра цветов тега">
              {TAG_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  role="option"
                  aria-selected={newColor === color}
                  className={`h-7 w-7 rounded-full border ${newColor === color ? 'ring-2 ring-tg-accent' : 'border-tg-border-soft'}`}
                  style={{ backgroundColor: color }}
                  aria-label={`Цвет ${color}`}
                  onClick={() => {
                    setNewColor(color);
                  }}
                />
              ))}
            </div>
          </div>
          <div className="flex gap-2">
            <Button type="button" onClick={() => void handleCreate()}>
              Добавить
            </Button>
            <Button type="button" variant="ghost" onClick={() => setCreating(false)}>
              Отмена
            </Button>
          </div>
        </div>
      ) : null}

      <div className="border-t border-tg-border-soft/60 pt-2">
        <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-tg-text-muted">Выбраны</p>
        {selected.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {selected.map((tag) => (
              <span key={tag.id} className={chipClass(true)} style={chipStyle(tag)}>
                <span className="pl-0.5">{tag.name}</span>
                <button
                  type="button"
                  className="mr-0.5 rounded-full px-1 leading-none opacity-80 hover:opacity-100"
                  aria-label={`Снять тег ${tag.name} с задачи`}
                  title="Снять с задачи"
                  onClick={() => {
                    unlink(tag.id);
                  }}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        ) : (
          <p className="text-xs text-tg-text-muted">Теги не выбраны</p>
        )}
      </div>

      <div className="border-t border-tg-border-soft/60 pt-2">
        <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-tg-text-muted">Доступны</p>
        {unselected.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {unselected.map((tag) => (
              <button
                key={tag.id}
                type="button"
                className={chipClass(false)}
                style={chipStyle(tag)}
                onClick={() => {
                  link(tag.id);
                }}
              >
                <span className="pl-0.5">{tag.name}</span>
                <span aria-hidden="true" className="mr-0.5 opacity-70">
                  +
                </span>
              </button>
            ))}
          </div>
        ) : (
          <p className="text-xs text-tg-text-muted">Все теги уже выбраны</p>
        )}
      </div>
    </div>
  );
}
